import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@lightning/ui/popover';
import { EXPERIENCE_LEVEL_DISPLAY } from '@/types/dojo';
import type {
  DimensionCell,
  DojoDimensionContribution,
  DojoDimensionProgress,
  DojoExperienceLevel,
  DojoMastery,
  DojoPracticeProgress,
  DojoProfile,
  FifteenCellMap,
  GCLDimension,
} from '@/types/dojo';
import { cn } from '@/lib/utils';
import { HOW_DIMENSION_COLOR, HOW_DIMENSION_TITLE } from '@/lib/howDimensions';

// Keep public placement provisional until the profile has either five rounds
// or enough cross-dimension confidence to make the right panel meaningful.
const PROFILE_ROUND_SIGNAL_THRESHOLD = 5;
const PROFILE_CONFIDENCE_SIGNAL_THRESHOLD = 0.35;
const PROFILE_DIMENSION_SIGNAL_THRESHOLD = 3;

const dimensionKeys: GCLDimension[] = ['KNOW', 'BEHAVE', 'RELATE', 'RECOGNIZE', 'PURSUE'];

const HOW_DIMENSION_PREVIEW: Record<GCLDimension, string> = {
  KNOW: 'How you take in and use information.',
  BEHAVE: 'How you act within structures and norms.',
  RELATE: 'How you form and sustain relationships.',
  RECOGNIZE: 'How you reward and hold others accountable.',
  PURSUE: 'How purpose shapes your choices.',
};


const SCENARIO_TITLE_HOW_PREFIX_RE =
  /^\s*HOW\s+(?:WE|YOU)\s+(?:KNOW|BEHAVE|RELATE|RECOGNIZE|PURSUE)\b\s*(?:[-—–‑‒−:|•·]\s*)?/i;
const SCENARIO_TITLE_BARE_DIMENSION_PREFIX_RE =
  /^\s*(?:KNOW|BEHAVE|RELATE|RECOGNIZE|PURSUE)\s*(?:[-—–‑‒−:|•·](?:\s+|$))/i;

function getScenarioDisplayTitle(
  title?: string | null,
  fallback = 'Practice session'
): string {
  let text = (title ?? '').replace(/\s+/g, ' ').trim();
  let didStrip = true;

  while (didStrip && text) {
    didStrip = false;
    const howPrefixMatch = text.match(SCENARIO_TITLE_HOW_PREFIX_RE);
    if (howPrefixMatch) {
      text = text.slice(howPrefixMatch[0].length).trim();
      didStrip = true;
      continue;
    }

    const bareDimensionMatch = text.match(SCENARIO_TITLE_BARE_DIMENSION_PREFIX_RE);
    if (bareDimensionMatch) {
      text = text.slice(bareDimensionMatch[0].length).trim();
      didStrip = true;
    }
  }

  return text || fallback;
}

type ProfileSignalState = 'no-signal' | 'calibrating' | 'pattern-emerging';

interface ProfileSignalSummary {
  state: ProfileSignalState;
  profileMap: FifteenCellMap | null;
  roundsCompleted: number;
  sampledDimensions: GCLDimension[];
  confidentDimensions: GCLDimension[];
  hasCompleteProfile: boolean;
  dimensionProgress: Partial<Record<GCLDimension, DojoDimensionProgress>> | null;
}

function isDimensionCell(cell: unknown): cell is DimensionCell {
  if (!cell || typeof cell !== 'object') return false;
  const maybeCell = cell as Partial<DimensionCell>;
  const hasKnownLevel =
    maybeCell.level === 'comply' || maybeCell.level === 'behave' || maybeCell.level === 'outbehave';

  return (
    hasKnownLevel && typeof maybeCell.confidence === 'number' && Number.isFinite(maybeCell.confidence)
  );
}

function isCompleteProfileMap(map: unknown): map is FifteenCellMap {
  if (!map || typeof map !== 'object') return false;
  const maybeMap = map as Partial<Record<GCLDimension, unknown>>;
  return dimensionKeys.every((dimension) => isDimensionCell(maybeMap[dimension]));
}

function getProfileSignalSummary(profile: DojoProfile | null): ProfileSignalSummary {
  const emptySummary: ProfileSignalSummary = {
    state: 'no-signal',
    profileMap: null,
    roundsCompleted: 0,
    sampledDimensions: [],
    confidentDimensions: [],
    hasCompleteProfile: false,
    dimensionProgress: null,
  };

  if (
    !profile ||
    typeof profile.rounds_completed !== 'number' ||
    !Number.isFinite(profile.rounds_completed)
  ) {
    return emptySummary;
  }

  const roundsCompleted = Math.max(0, Math.floor(profile.rounds_completed));

  if (!isCompleteProfileMap(profile.fifteen_cell_map)) {
    return {
      ...emptySummary,
      roundsCompleted,
    };
  }

  const profileMap = profile.fifteen_cell_map;
  const sampledDimensions = dimensionKeys.filter((dimension) => {
    const cell = profileMap[dimension];
    return cell.confidence > 0 || Boolean(cell.last_engaged);
  });
  const confidentDimensions = dimensionKeys.filter(
    (dimension) => profileMap[dimension].confidence >= PROFILE_CONFIDENCE_SIGNAL_THRESHOLD
  );
  const hasAnySignal = roundsCompleted > 0 || sampledDimensions.length > 0;
  const hasEnoughSignal =
    roundsCompleted >= PROFILE_ROUND_SIGNAL_THRESHOLD ||
    confidentDimensions.length >= PROFILE_DIMENSION_SIGNAL_THRESHOLD;

  return {
    state: hasEnoughSignal ? 'pattern-emerging' : hasAnySignal ? 'calibrating' : 'no-signal',
    profileMap,
    roundsCompleted,
    sampledDimensions,
    confidentDimensions,
    hasCompleteProfile: true,
    dimensionProgress: null,
  };
}

function getPracticeProgressSummary(
  profile: DojoProfile | null,
  practiceProgress: DojoPracticeProgress | null
): ProfileSignalSummary {
  const profileSummary = getProfileSignalSummary(profile);
  const dimensionProgress = practiceProgress?.dimension_progress ?? null;

  if (!practiceProgress || !dimensionProgress) {
    return profileSummary;
  }

  const roundsCompleted = Math.max(
    profileSummary.roundsCompleted,
    Math.max(0, Math.floor(practiceProgress.rounds_completed || 0))
  );
  const sampledDimensions = dimensionKeys.filter(
    (dimension) => dimensionProgress[dimension]?.has_signal
  );
  const confidentDimensions = dimensionKeys.filter(
    (dimension) =>
      (dimensionProgress[dimension]?.confidence ?? 0) >= PROFILE_CONFIDENCE_SIGNAL_THRESHOLD
  );
  const hasAnySignal = roundsCompleted > 0 || sampledDimensions.length > 0;
  const hasEnoughSignal =
    roundsCompleted >= PROFILE_ROUND_SIGNAL_THRESHOLD ||
    confidentDimensions.length >= PROFILE_DIMENSION_SIGNAL_THRESHOLD ||
    sampledDimensions.length >= 2;

  return {
    ...profileSummary,
    state: hasEnoughSignal ? 'pattern-emerging' : hasAnySignal ? 'calibrating' : 'no-signal',
    roundsCompleted,
    sampledDimensions,
    confidentDimensions,
    dimensionProgress,
  };
}

function getDimensionRead(progress?: DojoDimensionProgress): string {
  return progress?.working_read || 'There is no recorded practice evidence for this HOW yet. Choose a practice to explore it.';
}

function getContributionLabel(relationship?: string | null): string {
  if (relationship === 'practiced') return 'Practiced here';
  if (relationship === 'also_heard') return 'Also heard here';
  if (relationship === 'nearby') return 'Nearby';
  return 'Contributed';
}

function getMeaningfulEvidenceQuote(quote?: string | null): string | null {
  const normalized = quote?.replace(/\s+/g, ' ').trim();
  if (!normalized || normalized.length < 24) return null;

  const words = normalized.match(/[A-Za-z]{2,}/g) ?? [];
  return words.length >= 5 ? normalized : null;
}

function getContributionGroups(contributions: DojoDimensionContribution[] = []) {
  const groups: Array<{
    key: string;
    title: string;
    relationship?: string | null;
    evidenceQuote?: string | null;
    count: number;
    roundIds: string[];
  }> = [];
  const groupIndex = new Map<string, number>();

  contributions.forEach((contribution) => {
    const title = getScenarioDisplayTitle(contribution.scenario_title);
    const key = `${title}|${contribution.relationship || 'contributed'}`;
    const existingIndex = groupIndex.get(key);
    if (existingIndex !== undefined) {
      const existing = groups[existingIndex];
      existing.count += 1;
      if (contribution.round_id && !existing.roundIds.includes(contribution.round_id)) existing.roundIds.push(contribution.round_id);
      if (!existing.evidenceQuote) {
        existing.evidenceQuote = getMeaningfulEvidenceQuote(contribution.evidence_quote);
      }
      return;
    }

    groupIndex.set(key, groups.length);
    groups.push({
      key,
      title,
      relationship: contribution.relationship,
      evidenceQuote: getMeaningfulEvidenceQuote(contribution.evidence_quote),
      count: 1,
      roundIds: contribution.round_id ? [contribution.round_id] : [],
    });
  });

  return groups;
}

// Judge mastery tallies, when present, give each dimension a small strength bar.
// Anything the judge has seen more than once and rated well reads as stronger.
function getDimensionStrength(
  mastery: DojoMastery | null | undefined,
  dimension: GCLDimension
): number | null {
  const counts = mastery?.by_dimension?.[dimension];
  if (!counts) return null;
  const learning = counts.learning ?? 0;
  const review = counts.review ?? 0;
  const mastered = counts.mastered ?? 0;
  const fresh = counts.new ?? 0;
  const total = learning + review + mastered + fresh;
  if (total <= 0) return null;
  return Math.min(1, (learning * 0.35 + review * 0.7 + mastered) / total);
}

function getDimensionSignalStage(
  progress: DojoDimensionProgress | undefined,
  strength: number | null
): 0 | 1 | 2 {
  if (!progress?.has_signal && strength === null) return 0;
  const confidence = Math.max(0, Math.min(1, progress?.confidence ?? 0));
  return Math.max(confidence, strength ?? 0) >= PROFILE_CONFIDENCE_SIGNAL_THRESHOLD ? 2 : 1;
}

function getDimensionProgressLabel(stage: 0 | 1 | 2): string {
  if (stage === 0) return 'Not yet explored';
  if (stage === 1) return 'Pattern emerging';
  return 'Taking shape';
}

export function YourHowProgressHelp() {
  const progressStates = [
    ['Not yet explored', 1],
    ['Pattern emerging', 2],
    ['Taking shape', 3],
  ] as const;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[11px] leading-4 text-how-text-secondary/45 underline decoration-how-text-secondary/20 underline-offset-4 transition-colors hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/40"
        >
          <Info size={11} aria-hidden="true" />
          How progress works
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={10}
        className="w-64 rounded-xl border-how-space-deep/10 bg-white p-4 font-body text-how-space-deep shadow-popover"
      >
        <p className="text-[13px] font-medium leading-5">How progress works</p>
        <p className="mt-1 text-xs leading-5 text-how-text-secondary/65">
          Practice builds evidence across each dimension over time. These are signals, not scores.
        </p>
        <div className="mt-3 space-y-2.5">
          {progressStates.map(([label, filled]) => (
            <div key={label} className="flex items-center justify-between gap-4">
              <span className="text-xs leading-4 text-how-text-secondary/75">{label}</span>
              <span className="grid w-16 grid-cols-3 gap-1" aria-hidden="true">
                {[1, 2, 3].map((segment) => (
                  <span
                    key={segment}
                    className={cn(
                      'h-1 rounded-full',
                      segment <= filled
                        ? 'bg-how-space-mid/30'
                        : 'bg-how-space-deep/5'
                    )}
                  />
                ))}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface YourHowPanelProps {
  profile: DojoProfile | null;
  practiceProgress: DojoPracticeProgress | null;
  mastery?: DojoMastery | null;
  experienceLevel?: DojoExperienceLevel | null;
  onChangeLevel?: () => void;
  showTitle?: boolean;
}

/**
 * YOUR HOW. Same thresholds, copy, and lock behavior as the pre-units surface —
 * five rounds (or enough cross-dimension confidence) before it reflects anything
 * back. New here: the level chip and a per-dimension strength meter fed by the
 * judge's mastery counts.
 */
export function YourHowPanel({
  profile,
  practiceProgress,
  mastery = null,
  experienceLevel = null,
  onChangeLevel,
  showTitle = true,
}: YourHowPanelProps) {
  const [expandedHowDimension, setExpandedHowDimension] = useState<GCLDimension | null>(null);
  const dimensionInteractionRef = useRef<HTMLDivElement>(null);
  const signalSummary = getPracticeProgressSummary(profile, practiceProgress);

  useEffect(() => {
    if (!expandedHowDimension) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !dimensionInteractionRef.current?.contains(event.target)
      ) {
        setExpandedHowDimension(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpandedHowDimension(null);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expandedHowDimension]);

  const contributionGroupsByDimension = useMemo(
    () =>
      Object.fromEntries(
        dimensionKeys.map((dimension) => [
          dimension,
          getContributionGroups(practiceProgress?.dimension_progress?.[dimension]?.contributing_rounds),
        ])
      ) as Record<GCLDimension, ReturnType<typeof getContributionGroups>>,
    [practiceProgress?.dimension_progress]
  );

  const selectedProgress = expandedHowDimension
    ? signalSummary.dimensionProgress?.[expandedHowDimension]
    : undefined;
  const selectedContributionGroups = expandedHowDimension
    ? contributionGroupsByDimension[expandedHowDimension] ?? []
    : [];
  const visibleSelectedContributions = selectedContributionGroups.slice(0, 3);
  const hasSelectedEvidenceQuote = selectedContributionGroups.some(
    (contribution) => contribution.evidenceQuote
  );
  const hiddenSelectedContributionCount = selectedContributionGroups
    .slice(3)
    .reduce((count, contribution) => count + contribution.count, 0);
  const DetailHeading = showTitle ? 'h3' : 'h2';

  const renderDimensionDetail = (id: string, className?: string) => {
    if (!expandedHowDimension) return null;

    return (
      <div
        id={id}
        role="region"
        aria-labelledby={`${id}-title`}
        style={{ borderTopColor: HOW_DIMENSION_COLOR[expandedHowDimension] }}
        className={cn(
          'mx-auto mt-4 w-full max-w-[680px] rounded-2xl border border-t-2 border-how-space-deep/[0.06] bg-white/70 px-5 py-5 sm:px-6',
          className
        )}
      >
        <div className="mb-5 flex items-center gap-2 border-b border-how-space-deep/[0.06] pb-4">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-how-space-deep/[0.06]"
            style={{ backgroundColor: HOW_DIMENSION_COLOR[expandedHowDimension] }}
            aria-hidden="true"
          />
          <DetailHeading
            id={`${id}-title`}
            className="font-body text-base font-medium leading-5 tracking-[-0.015em] text-how-space-deep"
          >
            {HOW_DIMENSION_TITLE[expandedHowDimension]}
          </DetailHeading>
        </div>

        <div className="grid gap-5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:gap-6">
          <div>
            <p className="font-body text-sm font-medium leading-5 text-how-space-deep">
              What Howie is noticing
            </p>
            <p className="mt-2 text-sm leading-6 text-how-text-secondary/85">
              {getDimensionRead(selectedProgress)}
            </p>

            {selectedProgress?.next_edge && (
              <div className="mt-5 border-t border-how-space-deep/[0.06] pt-4">
                <p className="font-body text-sm font-medium leading-5 text-how-space-deep">
                  Try in your life
                </p>
                <p className="mt-2 text-sm leading-6 text-how-text-secondary/85">
                  {selectedProgress.next_edge}
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-how-space-deep/[0.06] pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
            <p className="font-body text-sm font-medium leading-5 text-how-space-deep">
              What this is based on
            </p>
            {selectedContributionGroups.length > 0 ? (
              <div className="mt-3 space-y-3">
                {visibleSelectedContributions.map((contribution) => (
                  <div key={contribution.key}>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-4">
                      <p className="text-sm font-medium leading-5 text-how-space-deep">
                        {contribution.roundIds.length === 1 ? (
                          <a className="underline decoration-how-space-deep/25 underline-offset-4 hover:decoration-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/35" href={`/dojo/round/${encodeURIComponent(contribution.roundIds[0])}`}>
                            {contribution.title}
                          </a>
                        ) : contribution.title}
                      </p>
                      <p className="shrink-0 text-xs leading-5 text-how-text-secondary/85">
                        {contribution.count > 1
                          ? `${contribution.count} sessions`
                          : getContributionLabel(contribution.relationship)}
                      </p>
                    </div>
                    {contribution.roundIds.length > 1 && (
                      <div className="mt-2 flex flex-wrap gap-3">
                        {contribution.roundIds.map((roundId, index) => (
                          <a key={roundId} href={`/dojo/round/${encodeURIComponent(roundId)}`} className="text-xs text-how-text-secondary underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-how-space-deep/35">
                            Review session {index + 1}
                          </a>
                        ))}
                      </div>
                    )}
                    {contribution.evidenceQuote && (
                      <p className="mt-1 text-[13px] leading-5 text-how-text-secondary/85">
                        “{contribution.evidenceQuote}”
                      </p>
                    )}
                  </div>
                ))}
                {!hasSelectedEvidenceQuote && (
                  <p className="text-[13px] leading-5 text-how-text-secondary/85">
                    There is not enough clear evidence to quote yet.
                  </p>
                )}
                {hiddenSelectedContributionCount > 0 && (
                  <p className="text-[13px] leading-5 text-how-text-secondary/85">
                    {hiddenSelectedContributionCount} more practice session
                    {hiddenSelectedContributionCount === 1 ? '' : 's'} contributed.
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-2 text-[13px] leading-5 text-how-text-secondary/85">
                There is not enough clear evidence here yet.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <aside className="w-full" aria-label="Your HOW dimensions">
      {showTitle && (
        <h2 className="font-body text-base font-medium leading-6 text-how-space-deep">
          Your HOW
        </h2>
      )}

      <div ref={dimensionInteractionRef}>
        <div className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-5', showTitle && 'mt-5')}>
          {dimensionKeys.map((dimension) => {
            const progress = signalSummary.dimensionProgress?.[dimension];
            const strength = getDimensionStrength(mastery, dimension);
            const signalStage = getDimensionSignalStage(progress, strength);
            const progressLabel = getDimensionProgressLabel(signalStage);
            const filledSegments = signalStage + 1;
            const isExpanded = expandedHowDimension === dimension;

            return (
              <Fragment key={dimension}>
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  aria-controls={
                    isExpanded
                      ? `your-how-dimension-detail-mobile-${dimension.toLowerCase()} your-how-dimension-detail-desktop-${dimension.toLowerCase()}`
                      : undefined
                  }
                  onClick={() => setExpandedHowDimension(isExpanded ? null : dimension)}
                  style={
                    isExpanded
                      ? { borderTopColor: HOW_DIMENSION_COLOR[dimension] }
                      : undefined
                  }
                  className={cn(
                    'group flex min-h-[7.5rem] flex-col rounded-2xl border border-t-2 px-4 py-3.5 text-left transition-[transform,background-color,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lift focus-visible:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/70 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:shadow-lift',
                    isExpanded
                      ? '-translate-y-0.5 border-how-space-deep/10 bg-white shadow-lift'
                      : expandedHowDimension
                        ? 'border-how-space-deep/[0.04] bg-white/45 hover:border-how-space-deep/15 hover:bg-white/85'
                        : 'border-how-space-deep/[0.06] bg-white/80 shadow-surface hover:border-how-space-deep/15 hover:bg-white'
                  )}
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-how-space-deep/[0.06] transition-[transform,box-shadow] duration-200 group-hover:scale-110 group-hover:ring-how-space-deep/15 group-focus-visible:scale-110 group-focus-visible:ring-how-space-deep/15"
                    style={{ backgroundColor: HOW_DIMENSION_COLOR[dimension] }}
                    aria-hidden="true"
                  />
                  <span className="mt-2.5 block font-body text-[0.95rem] font-medium leading-5 tracking-[-0.015em] text-how-space-deep">
                    {HOW_DIMENSION_TITLE[dimension]}
                  </span>
                  <span className="mt-1.5 block text-[13px] leading-5 text-how-text-secondary">
                    {HOW_DIMENSION_PREVIEW[dimension]}
                  </span>

                  <span className="mt-auto block pt-3">
                    <span className="block text-[11px] leading-4 tracking-[0.01em] text-how-text-secondary/85">
                      {progressLabel}
                    </span>
                    <span
                      className="mt-1.5 grid grid-cols-3 gap-1"
                      role="img"
                      aria-label={`${HOW_DIMENSION_TITLE[dimension]}: ${progressLabel}`}
                    >
                      {[1, 2, 3].map((segment) => (
                        <span
                          key={segment}
                          className={cn(
                            'block h-1 rounded-full transition-colors duration-300',
                            segment <= filledSegments
                              ? 'bg-how-space-mid/30'
                              : 'bg-how-space-deep/5'
                          )}
                        />
                      ))}
                    </span>
                  </span>
                </button>

                {isExpanded &&
                  renderDimensionDetail(
                    `your-how-dimension-detail-mobile-${dimension.toLowerCase()}`,
                    'sm:col-span-2 lg:hidden'
                  )}
              </Fragment>
            );
          })}
        </div>

        {expandedHowDimension &&
          renderDimensionDetail(
            `your-how-dimension-detail-desktop-${expandedHowDimension.toLowerCase()}`,
            'hidden lg:block'
          )}
      </div>

      {experienceLevel && (
        <div className="mt-6 flex items-center gap-2 border-t border-how-space-deep/[0.06] pt-4 text-xs text-how-text-secondary/65">
          <span>Level: {EXPERIENCE_LEVEL_DISPLAY[experienceLevel]}</span>
          {onChangeLevel && (
            <button
              type="button"
              onClick={onChangeLevel}
              className="text-how-accent-warm transition-colors hover:underline"
            >
              change
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

export default YourHowPanel;
